import { API_CONFIG, ERROR_MESSAGES } from '../config';

interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

class ApiService {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}/api/${API_CONFIG.API_VERSION}/${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Add authorization header if token exists
    const token = localStorage.getItem('auth_token');
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.handleErrorResponse(response);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(ERROR_MESSAGES.TIMEOUT_ERROR);
        }
        throw new Error(error.message || ERROR_MESSAGES.UNKNOWN_ERROR);
      }
      throw error;
    }
  }

  private async handleErrorResponse(response: Response): Promise<ApiError> {
    let errorData: any;
    
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: 'Unknown error' };
    }

    // Extract server message from various possible response formats
    let serverMessage = 'Unknown error';
    
    if (errorData.message) {
      serverMessage = errorData.message;
    } else if (errorData.error) {
      serverMessage = errorData.error;
    } else if (errorData.errors && typeof errorData.errors === 'object') {
      // Handle validation errors - show first error message
      const firstError = Object.values(errorData.errors)[0];
      if (Array.isArray(firstError) && firstError.length > 0) {
        serverMessage = firstError[0];
      } else if (typeof firstError === 'string') {
        serverMessage = firstError;
      }
    }

    const error: ApiError = {
      message: serverMessage,
      status: response.status,
      errors: errorData.errors,
    };

    return error;
  }

  private getErrorMessage(status: number): string {
    switch (status) {
      case 401:
        return ERROR_MESSAGES.UNAUTHORIZED;
      case 403:
        return ERROR_MESSAGES.FORBIDDEN;
      case 404:
        return ERROR_MESSAGES.NOT_FOUND;
      case 422:
        return ERROR_MESSAGES.VALIDATION_ERROR;
      case 500:
        return ERROR_MESSAGES.SERVER_ERROR;
      default:
        return ERROR_MESSAGES.UNKNOWN_ERROR;
    }
  }

  // GET request
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const url = params ? this.buildUrlWithParams(endpoint, params) : endpoint;
    return this.request<T>(url, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Build URL with query parameters
  private buildUrlWithParams(endpoint: string, params: Record<string, any>): string {
    const url = new URL(`${this.baseUrl}/api/${API_CONFIG.API_VERSION}/${endpoint}`);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => url.searchParams.append(key, item.toString()));
        } else {
          url.searchParams.append(key, value.toString());
        }
      }
    });

    // Return only the endpoint with query parameters, not the full URL
    return endpoint + url.search;
  }

  // Upload file with additional data
  async uploadFileWithData<T>(endpoint: string, formData: FormData, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new Error(ERROR_MESSAGES.UNKNOWN_ERROR));
          }
        } else {
          reject(new Error(this.getErrorMessage(xhr.status)));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error(ERROR_MESSAGES.NETWORK_ERROR));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error(ERROR_MESSAGES.TIMEOUT_ERROR));
      });

      xhr.open('POST', `${this.baseUrl}/api/${API_CONFIG.API_VERSION}/${endpoint}`);
      
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.timeout = this.timeout;
      xhr.send(formData);
    });
  }

  // Upload file
  async uploadFile<T>(endpoint: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new Error(ERROR_MESSAGES.UNKNOWN_ERROR));
          }
        } else {
          reject(new Error(this.getErrorMessage(xhr.status)));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error(ERROR_MESSAGES.NETWORK_ERROR));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error(ERROR_MESSAGES.TIMEOUT_ERROR));
      });

      xhr.open('POST', `${this.baseUrl}/api/${API_CONFIG.API_VERSION}/${endpoint}`);
      
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.timeout = this.timeout;
      xhr.send(formData);
    });
  }

  // Download file
  async downloadFile(endpoint: string, filename?: string): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/api/${API_CONFIG.API_VERSION}/${endpoint}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(this.getErrorMessage(response.status));
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Export commonly used API endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: 'auth/login',
  LOGOUT: 'logout',
  ME: 'auth/me',
  
  // Dashboard
  DASHBOARD_STATS: 'dashboard/stats',
  DASHBOARD_AGENT_STATS: 'dashboard/agent-stats',
  
  // Profile
  PROFILE: 'profile',
  PROFILE_PASSWORD: 'profile/password',
  
  // Users
  USERS: 'users',
  USERS_BULK_ASSIGN_MODULES: 'users/bulk-assign-modules',
  USERS_BULK_REMOVE_MODULES: 'users/bulk-remove-modules',
  
  // Groups
  GROUPS: 'groups',
  GROUPS_BULK_ASSIGN_MODULES: 'groups/bulk-assign-modules',
  GROUPS_BULK_REMOVE_MODULES: 'groups/bulk-remove-modules',
  
  // Modules
  MODULES: 'modules',
  
  // Call Logs
  CALL_LOGS: 'call-logs',
  CALL_LOGS_COMPLETE: 'call-logs/complete',
  CALL_LOGS_SYNC: 'call-logs/sync',
  
  // Contacts
  CONTACTS: 'contacts',
  CONTACTS_FIND_BY_PHONE: 'contacts/find-by-phone',
  CONTACTS_SYNC: 'contacts/sync',
  
  // Reporting
  REPORTING_CALL_ANALYTICS: 'reporting/call-analytics',
  REPORTING_AGENT_PERFORMANCE: 'reporting/agent-performance',
  REPORTING_CALL_TRENDS: 'reporting/call-trends',
  REPORTING_CALL_DURATION_ANALYTICS: 'reporting/call-duration-analytics',
  REPORTING_CALL_STATUS_DISTRIBUTION: 'reporting/call-status-distribution',
  REPORTING_TOP_PERFORMING_AGENTS: 'reporting/top-performing-agents',
  REPORTING_CALL_COMPLETION_RATES: 'reporting/call-completion-rates',
  REPORTING_EXPORT_CALL_REPORT: 'reporting/export-call-report',
  
  // Agent Performance Reports
  AGENT_PERFORMANCE_REPORT_CALL_ANALYTICS: 'agent-performance-report/call-analytics',
  AGENT_PERFORMANCE_REPORT_AGENT_PERFORMANCE: 'agent-performance-report/agent-performance',
  AGENT_PERFORMANCE_REPORT_CALL_STATUS_DISTRIBUTION: 'agent-performance-report/call-status-distribution',
  AGENT_PERFORMANCE_REPORT_TOP_PERFORMING_AGENTS: 'agent-performance-report/top-performing-agents',
  AGENT_PERFORMANCE_REPORT_CALL_COMPLETION_RATES: 'agent-performance-report/call-completion-rates',
  AGENT_PERFORMANCE_REPORT_EXPORT_CALL_REPORT: 'agent-performance-report/export-call-report',
  AGENT_PERFORMANCE_REPORT_DASHBOARD_REPORT: 'agent-performance-report/dashboard-report',
  AGENT_PERFORMANCE_REPORT_SEARCH_AGENTS: 'agent-performance-report/search-agents',
  AGENT_PERFORMANCE_REPORT_AGENT_CALL_COUNT: 'agent-performance-report/agent-call-count',
  AGENT_PERFORMANCE_REPORT_AGENT_CALL_LIST: 'agent-performance-report/agent-call-list',
} as const;

export default apiService; 